/**
 * Institution Insumos Hooks
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tokenStorage } from '@/lib/api'
import * as service from './institution-insumos.service'
import type {
  CreatePriorityInput,
  CreateStudyTypeInput,
} from './institution-insumos.service'

const keys = {
  all: ['institution-insumos'] as const,
  studyTypes: (id: string) => [...keys.all, 'study-types', id] as const,
  priorities: (id: string) => [...keys.all, 'priorities', id] as const,
}

// Study types
export function useInstitutionStudyTypes(institutionId: string | null) {
  const hasToken = !!tokenStorage.getAccessToken()
  return useQuery({
    queryKey: institutionId ? keys.studyTypes(institutionId) : ['noop'],
    queryFn: () => service.listInstitutionStudyTypes(institutionId as string),
    enabled: hasToken && !!institutionId,
  })
}

export function useCreateInstitutionStudyType(institutionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateStudyTypeInput) =>
      service.createInstitutionStudyType(institutionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.studyTypes(institutionId) })
    },
  })
}

export function useDeleteInstitutionStudyType(institutionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (resourceId: string) =>
      service.deleteInstitutionStudyType(institutionId, resourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.studyTypes(institutionId) })
    },
  })
}

// Priorities
export function useInstitutionPriorities(institutionId: string | null) {
  const hasToken = !!tokenStorage.getAccessToken()
  return useQuery({
    queryKey: institutionId ? keys.priorities(institutionId) : ['noop'],
    queryFn: () => service.listInstitutionPriorities(institutionId as string),
    enabled: hasToken && !!institutionId,
  })
}

export function useCreateInstitutionPriority(institutionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreatePriorityInput) =>
      service.createInstitutionPriority(institutionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.priorities(institutionId) })
    },
  })
}

export function useDeleteInstitutionPriority(institutionId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (resourceId: string) =>
      service.deleteInstitutionPriority(institutionId, resourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.priorities(institutionId) })
    },
  })
}
